import { computed, ref } from 'vue'
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { faEllipsisVertical, faPlus } from '@fortawesome/pro-regular-svg-icons'

import { AppRoute } from '@shared/config'

import { useTasksKanbanStore } from '../store'

interface TaskRelatedCreateReturn {
  refCanCreateRelatedTask: ComputedRef<boolean>
  refContainer: Ref<HTMLElement | null>
  refFloating: Ref<HTMLElement | null>
  refIsOpen: Ref<boolean>
  refFloatingStyles: Ref<CSSProperties>
  refTriggerIcon: IconDefinition
  refCreateIcon: IconDefinition
  toggle: () => void
  createRelatedTask: () => void
}

export const useTaskRelatedCreate = (): TaskRelatedCreateReturn => {
  const route = useRoute()
  const tasksKanbanStore = useTasksKanbanStore()
  const refIsOpen = ref(false)
  const refContainer = ref<HTMLElement | null>(null)
  const refFloating = ref<HTMLElement | null>(null)

  const { floatingStyles: refFloatingStyles } = useFloating(refContainer, refFloating, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const refTaskId = computed<number | null>(() => {
    const id = Number(route.params.id)
    return Number.isSafeInteger(id) && id > 0 ? id : null
  })

  const refCanCreateRelatedTask = computed<boolean>(() => {
    return !!refTaskId.value
      && !!tasksKanbanStore.getCustomer?.id
      && !!tasksKanbanStore.getRelatedTo?.id
  })

  const toggle = (): void => {
    refIsOpen.value = !refIsOpen.value
  }

  const createRelatedTask = (): void => {
    const customerId = tasksKanbanStore.getCustomer?.id
    const relatedTo = tasksKanbanStore.getRelatedTo
    const taskId = refTaskId.value

    if (!customerId || !relatedTo || !taskId) return

    refIsOpen.value = false

    void navigateTo({
      path: AppRoute.TasksKanbanCreation,
      query: {
        customer_id: String(customerId),
        related_to_id: String(relatedTo.id),
        related_to_type: relatedTo.type,
        source_task_id: String(taskId),
      },
    })
  }

  onClickOutside(refContainer, () => {
    refIsOpen.value = false
  })

  return {
    refCanCreateRelatedTask,
    refContainer,
    refFloating,
    refIsOpen,
    refFloatingStyles,
    refTriggerIcon: faEllipsisVertical,
    refCreateIcon: faPlus,
    toggle,
    createRelatedTask,
  }
}
